// Detección híbrida: 1) ua-parser-js → 2) UA-CH → 3) fallback
// Requiere: npm i ua-parser-js
import { UAParser } from "ua-parser-js";

let _cached = null;

/** Devuelve true si el valor está ausente o vacío. */
function isMissing(v) {
  return v == null || (typeof v === "string" && v.trim() === "");
}

/** Copia campos faltantes desde src a dst sin sobrescribir valores ya presentes. */
function mergeIfMissing(dst, src) {
  if (!src || typeof src !== "object") return dst;
  for (const k of Object.keys(src)) {
    const sv = src[k];
    const dv = dst[k];
    if (sv && typeof sv === "object" && !Array.isArray(sv)) {
      dst[k] = mergeIfMissing(dv && typeof dv === "object" ? dv : {}, sv);
    } else if (isMissing(dv)) {
      dst[k] = sv;
    }
  }
  return dst;
}

export async function getDeviceInfo({ uaChTimeoutMs = 150 } = {}) {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      fuentes: ["server"],
      movil: null,
      so: null,
      navegador: null,
    };
  }
  if (_cached) return _cached;

  const nav = navigator;
  const agenteUsuario = String(nav.userAgent || "");
  const idioma = (nav.languages && nav.languages[0]) || nav.language || "";
  const relacionPixel = window.devicePixelRatio || 1;
  const anchoPantalla = window.screen?.width ?? null;
  const altoPantalla = window.screen?.height ?? null;
  const tactil = (nav.maxTouchPoints || 0) > 0;

  const fuentes = [];

  // 1) ua-parser-js
  const analisis = new UAParser(agenteUsuario).getResult();
  let info = {
    fuentes: null,
    navegador: {
      version: [analisis.browser?.name, analisis.browser?.version]
        .filter(Boolean)
        .join(" ") || null,
    },
    informacion: {
      tipo: analisis.device?.type || null,
      modelo: analisis.device?.model || null,
      marca: analisis.device?.vendor || null,
      esMovil: ["mobile", "tablet"].includes(analisis.device?.type || ""),
      arquitectura: analisis.cpu?.architecture || null,
      pantalla: {
        ancho: anchoPantalla,
        alto: altoPantalla,
        relacionPixel,
        tactil,
      },
      sistemaOperativo: {
        nombre:
          [analisis.os?.name, analisis.os?.version].filter(Boolean).join(" ") ||
          null,
      },
      idioma,
    },
    servidorCliente: agenteUsuario,
  };
  fuentes.push("ua-parser-js");

  // 2) UA-CH (completa solo faltantes)
  try {
    const uad = nav.userAgentData;
    if (uad && typeof uad.getHighEntropyValues === "function") {
      const hi = await Promise.race([
        uad.getHighEntropyValues([
          "platform",
          "platformVersion",
          "model",
          "architecture",
          "bitness",
          "uaFullVersion",
        ]),
        new Promise((res) => setTimeout(() => res(null), uaChTimeoutMs)),
      ]);
      if (hi) {
        const desdeUaCh = {
          navegador: {
            version: hi.uaFullVersion || null,
          },
          informacion: {
            modelo: hi.model || null,
            esMovil: isMissing(info.informacion.esMovil) ? !!uad.mobile : null,
            arquitectura:
              hi.architecture || info.informacion.arquitectura || null,
            bits: hi.bitness || null,
            sistemaOperativo: {
              nombre: hi.platform || null,
              version: hi.platformVersion || null,
            },
          },
          marcas: uad.brands || null,
        };
        info = mergeIfMissing(info, desdeUaCh);
        fuentes.push("UA-CH");
      }
    }
  } catch {
    //
  }

  // 3) Fallback por UA + heurísticas (solo faltantes)
  const esIPadOS = /Macintosh/i.test(agenteUsuario) && tactil;
  let soFallback;
  if (/Windows NT/i.test(agenteUsuario)) soFallback = "Windows";
  else if (/Android/i.test(agenteUsuario)) soFallback = "Android";
  else if (/iPhone|iPad|iPod/i.test(agenteUsuario) || esIPadOS)
    soFallback = "iOS";
  else if (/Mac OS X|Macintosh/i.test(agenteUsuario)) soFallback = "macOS";
  else if (/Linux/i.test(agenteUsuario)) soFallback = "Linux";

  let navegadorFallback;
  if (/Edg\//i.test(agenteUsuario)) navegadorFallback = "Edge";
  else if (/OPR\//i.test(agenteUsuario) || /Opera/i.test(agenteUsuario))
    navegadorFallback = "Opera";
  else if (/Firefox\//i.test(agenteUsuario)) navegadorFallback = "Firefox";
  else if (/Chrome\/|CriOS\//i.test(agenteUsuario))
    navegadorFallback = "Chrome";
  else if (/Safari\//i.test(agenteUsuario)) navegadorFallback = "Safari";

  const faltantes = {
    informacion: {
      esMovil:
        /Android|iPhone|iPod|Mobile/i.test(agenteUsuario) ||
        esIPadOS ||
        /iPad/i.test(agenteUsuario),
        sistemaOperativo: {
          nombre: soFallback || null
        }
    },
    navegador: {
      nombre: navegadorFallback || null
    },
  };
  const antes = JSON.stringify(info);
  info = mergeIfMissing(info, faltantes);
  if (JSON.stringify(info) !== antes) fuentes.push("fallback");

  // —— Normalización: marcar modelo censurado de PWA/UA-CH ——
  const modelo = info?.informacion?.modelo;
  if (typeof modelo === 'string' && modelo.trim().length <= 1) {
    info.informacion.modelo = "PWA";
  } else {
    info.informacion = { ...info.informacion };
  }

  info.fuentes = fuentes;
  _cached = info;
  return _cached;
}

export function resetDeviceInfoCache() {
  _cached = null;
}

export default { getDeviceInfo, resetDeviceInfoCache };
